import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Scale, ArrowRight, CheckCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { legalServices as mockServices } from '../data/mock';
import { contentAPI } from '../services/api';

const LegalAdvisory = () => {
  const [legalServices, setLegalServices] = useState(mockServices);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('legal_services').then(res => {
      if (Array.isArray(res.data) && res.data.length) setLegalServices(res.data);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1508791290064-c27cc1ef7a9a?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Legal Advisory"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Legal Advisory</h1>
          <p className="text-xl text-gray-200 max-w-2xl">
            Expert legal support for licensing, contracts, and regulatory compliance
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Protect Your Renewable Energy Investments</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our energy law specialists provide comprehensive legal advisory services for renewable energy projects. From licensing and tariff adoption to contract disputes and regulatory compliance, we safeguard your interests at every step.
            </p>
          </div>

          {/* Legal Services Accordion */}
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {legalServices.map((service) => (
                <AccordionItem
                  key={service.id}
                  value={service.id}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                >
                  <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex items-center gap-4 text-left w-full">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Scale className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed mb-4">{service.description}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-emerald-600 font-semibold hover:text-emerald-700 transition-colors duration-200"
                    >
                      Request Legal Review
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Our Legal Team?</h2>
            <p className="text-lg text-gray-600">Experienced energy law specialists you can trust</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Energy Law Expertise',
                desc: 'Specialized knowledge of power sector regulations and renewable energy law',
                icon: CheckCircle
              },
              {
                title: 'Proven Results',
                desc: 'Successfully resolved complex PPA disputes and regulatory challenges',
                icon: CheckCircle
              },
              {
                title: 'Client-Focused',
                desc: 'Tailored legal strategies aligned with your business objectives',
                icon: CheckCircle
              }
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-8 shadow-lg text-center transform hover:-translate-y-2 transition-all duration-300">
                <item.icon className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specializations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Legal Specializations</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contractual Matters</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">PPA drafting and negotiation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">EPC contract review</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Force majeure claims</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Change in law provisions</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Regulatory Compliance</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Licensing requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Tariff adoption processes</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">License surrender procedures</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Hypothecation permissions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Legal Advisory?
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Protect your renewable energy investments with expert legal support
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Request Legal Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LegalAdvisory;
